// Tasks page helpers (2026-10-12, Lab-only behind TASKS_ENABLED). The type
// definitions, the display ID, the order-reference shapes and the Eastern
// "due" rule live in @cxhub/shared (shared/src/tasks.js), the same module the
// server validates with.
import {
  splitOrderRefs, TASK_TIMEZONE, TASK_SORTS, DEFAULT_TASK_SORT, TASK_CREATED_PRESETS, TASK_ACTION_PRESETS, TASK_STATUS_LABELS,
  TASK_DUE_TRIGGERS, taskDueTriggerDisplay, fifthBusinessDay, easternDate, addBusinessDays, TASK_BUSINESS_DAYS_AHEAD,
  TASK_DUE_BASES, FIFTH_BUSINESS_DAY, FIFTH_BUSINESS_DAY_FROM_LAST_SCAN, conversationMatchesToggles,
} from '@cxhub/shared'

// /tasks opens the app on the Tasks page, only when the feature is on and the
// requester can use it; otherwise it falls back to '/' like any unknown path.
export const TASKS_PATH = '/tasks'
export function isTasksPath(pathname) {
  return /^\/tasks\/?$/.test(pathname ?? '')
}
export function canSeeTasks(me) {
  return Boolean(me?.tasksEnabled && me?.canUseTasks)
}
export function navPageForTasksPath(pathname, me) {
  return canSeeTasks(me) && isTasksPath(pathname) ? 'tasks' : null
}

// The list's sort (2026-10-13): Action Needed By ascending by default, set by
// the two sortable headers. Not remembered: a reload and Clear filters reset
// it. The keys and the default live in @cxhub/shared with the server's.
export { TASK_SORTS, DEFAULT_TASK_SORT }
export const TASK_SORT_COLUMNS = Object.freeze({
  action: Object.freeze({ asc: TASK_SORTS.actionAsc, desc: TASK_SORTS.actionDesc }),
  created: Object.freeze({ asc: TASK_SORTS.createdAsc, desc: TASK_SORTS.createdDesc }),
})
// 'asc' / 'desc' when the list is sorted by this column, else null.
export function taskSortDirection(sort, column) {
  const c = TASK_SORT_COLUMNS[column]
  if (!c) return null
  return sort === c.asc ? 'asc' : sort === c.desc ? 'desc' : null
}
// Clicking a header: the active one flips direction; another one sorts by
// that column ascending.
export function nextTaskSort(sort, column) {
  const c = TASK_SORT_COLUMNS[column]
  if (!c) return sort
  return sort === c.asc ? c.desc : c.asc
}

// created / action: a preset key (TASK_CREATED_PRESETS / TASK_ACTION_PRESETS)
// or '' for All. q: one box over Description, Order Number and the ID.
// closed (2026-10-13): "Show closed tasks", closed ones instead of open ones.
export const EMPTY_TASK_FILTERS = Object.freeze({ createdBy: [], created: '', action: '', q: '', closed: false })

export function hasTaskFilters(f = EMPTY_TASK_FILTERS) {
  return Boolean(f.createdBy?.length || f.created || f.action || f.q || f.closed)
}

// The filters are remembered per browser, like the Parked filters; the sort
// is not. Storage can be missing or throw (private windows): reads fall back
// to the defaults, writes are skipped. A stored view from before the presets
// (date ranges, the missing-date box, the ID box) keeps only what still
// exists.
export const TASK_VIEW_KEY = 'cxhub:tasks:view'
export function readTaskFilters(storage = globalThis.localStorage) {
  try {
    const raw = storage?.getItem(TASK_VIEW_KEY)
    const stored = (raw ? JSON.parse(raw) : null)?.filters ?? {}
    return {
      createdBy: Array.isArray(stored.createdBy) ? stored.createdBy.filter((v) => typeof v === 'string') : [],
      created: TASK_CREATED_PRESETS.some((p) => p.key === stored.created) ? stored.created : '',
      action: TASK_ACTION_PRESETS.some((p) => p.key === stored.action) ? stored.action : '',
      q: typeof stored.q === 'string' ? stored.q : '',
      closed: stored.closed === true,
    }
  } catch {
    return { ...EMPTY_TASK_FILTERS, createdBy: [] }
  }
}
export function writeTaskFilters(filters, storage = globalThis.localStorage) {
  try { storage?.setItem(TASK_VIEW_KEY, JSON.stringify({ filters })) } catch { /* not remembered */ }
}

// The GET /api/tasks query string for the filters and sort.
export function taskListQuery(filters = EMPTY_TASK_FILTERS, sort = DEFAULT_TASK_SORT) {
  const p = new URLSearchParams()
  if (filters.createdBy?.length) p.set('createdBy', filters.createdBy.join(','))
  if (filters.created) p.set('created', filters.created)
  if (filters.action) p.set('action', filters.action)
  if (filters.q && String(filters.q).trim()) p.set('q', String(filters.q).trim())
  if (filters.closed) p.set('closed', 'true')
  if (sort && sort !== DEFAULT_TASK_SORT) p.set('sort', sort)
  const s = p.toString()
  return s ? `?${s}` : ''
}

// "Oct 12, 2026, 10:04 AM" in America/New_York.
export function formatTaskTimestamp(iso) {
  if (!iso) return ''
  return new Intl.DateTimeFormat('en-US', { timeZone: TASK_TIMEZONE, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(iso))
}

// A calendar date and optional time exactly as entered: "Oct 20, 2026" or
// "Oct 20, 2026, 9:15 AM". Never shifted through a time zone.
export function formatActionNeededBy(date, time) {
  if (!date) return ''
  const [y, m, d] = date.split('-').map(Number)
  const day = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(Date.UTC(y, m - 1, d)))
  if (!time) return day
  const [hh, mm] = time.split(':').map(Number)
  const h12 = hh % 12 === 0 ? 12 : hh % 12
  return `${day}, ${h12}:${String(mm).padStart(2, '0')} ${hh < 12 ? 'AM' : 'PM'}`
}

// What the Action Needed By column and the details show (2026-10-13): the
// trigger's wording when the task has one, else the date as entered.
export function formatTaskActionNeededBy(task) {
  return taskDueTriggerDisplay(task?.dueTrigger) ?? formatActionNeededBy(task?.actionNeededByDate, task?.actionNeededByTime)
}

// The form's optional, mutually exclusive "Action Needed By" options
// (2026-10-13), in order: Replacement Order Tracking is Available, 5th
// Business Day, 5th Business Day From Last Scan, Return Document Created
// (2026-10-14). values are the form's API-keyed
// values; each helper returns the next values. The first two are stored as
// dueBasis (with lastScanDate for the second) so editing shows what was used;
// the third is dueTrigger. Checking one unchecks the others.
export const TRACKING_TRIGGER = 'replacement_tracking_available'
export const TRACKING_TRIGGER_LABEL = TASK_DUE_TRIGGERS[TRACKING_TRIGGER].label
export const FIFTH_BUSINESS_DAY_LABEL = TASK_DUE_BASES[FIFTH_BUSINESS_DAY].label
export const FIFTH_BUSINESS_DAY_FROM_LAST_SCAN_LABEL = TASK_DUE_BASES[FIFTH_BUSINESS_DAY_FROM_LAST_SCAN].label
export { FIFTH_BUSINESS_DAY, FIFTH_BUSINESS_DAY_FROM_LAST_SCAN }
export function isFifthBusinessDayChecked(values) {
  return values?.dueBasis === FIFTH_BUSINESS_DAY
}
export function isFromLastScanChecked(values) {
  return values?.dueBasis === FIFTH_BUSINESS_DAY_FROM_LAST_SCAN
}
export function isTrackingTriggerChecked(values) {
  return values?.dueTrigger === TRACKING_TRIGGER
}
const NO_OPTION = { dueBasis: null, lastScanDate: '', dueTrigger: null }
// Checking it fills the date with five business days after today (Eastern);
// unchecking it leaves the date.
export function setFifthBusinessDay(values, checked, now = new Date()) {
  if (!checked) return { ...values, dueBasis: null }
  return { ...values, ...NO_OPTION, dueBasis: FIFTH_BUSINESS_DAY, actionNeededByDate: fifthBusinessDay(easternDate(now)) }
}
// Checking it shows the required Last Scan Date; the date fills once one is
// entered (setLastScanDate()). Unchecking it hides the Last Scan Date and
// leaves the date.
export function setFromLastScan(values, checked) {
  if (!checked) return { ...values, dueBasis: null, lastScanDate: '' }
  return { ...values, ...NO_OPTION, dueBasis: FIFTH_BUSINESS_DAY_FROM_LAST_SCAN, actionNeededByDate: '' }
}
// The Action Needed By date for a Last Scan Date: the 5th business day after
// it (the scan day not counted); '' while there is none or it isn't a date.
export function dateFromLastScan(lastScanDate) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(lastScanDate ?? '')) ? addBusinessDays(lastScanDate, TASK_BUSINESS_DAYS_AHEAD) : ''
}
// Entering or changing the Last Scan Date recalculates the date.
export function setLastScanDate(values, lastScanDate) {
  return { ...values, lastScanDate, actionNeededByDate: dateFromLastScan(lastScanDate) }
}
// Checking it clears (and the form hides) the date and time; unchecking it
// brings the date back, empty.
export function setTrackingTrigger(values, checked) {
  return setDueTrigger(values, TRACKING_TRIGGER, checked)
}
// The fourth option (2026-10-14): "Return Document Created" stands in for the
// date like the tracking trigger, checked by its own 12-hour job.
export const RETURN_DOCUMENT_TRIGGER = 'return_document_created'
export const RETURN_DOCUMENT_TRIGGER_LABEL = TASK_DUE_TRIGGERS[RETURN_DOCUMENT_TRIGGER].label
export function isReturnDocumentTriggerChecked(values) {
  return values?.dueTrigger === RETURN_DOCUMENT_TRIGGER
}
export function setReturnDocumentTrigger(values, checked) {
  return setDueTrigger(values, RETURN_DOCUMENT_TRIGGER, checked)
}
// Either trigger hides the date and time.
export function hasDueTrigger(values) {
  return Boolean(values?.dueTrigger)
}
function setDueTrigger(values, trigger, checked) {
  if (!checked) return { ...values, dueTrigger: null, actionNeededByDate: '', actionNeededByTime: '' }
  return { ...values, ...NO_OPTION, dueTrigger: trigger, actionNeededByDate: '', actionNeededByTime: '' }
}
// The agent changing the date by hand unchecks 5th Business Day and 5th
// Business Day From Last Scan (hiding the Last Scan Date).
export function setActionNeededByDate(values, date) {
  return { ...values, actionNeededByDate: date, dueBasis: null, lastScanDate: '' }
}
// The clear X beside each field (2026-10-14). Clearing the date leaves the
// task with no date (allowed) and clears its time; like any change to the
// date by hand it unchecks a 5th Business Day option. Clearing the Last Scan
// Date empties the date it filled.
export function clearActionNeededByDate(values) {
  return { ...setActionNeededByDate(values, ''), actionNeededByTime: '' }
}
export function clearActionNeededByTime(values) {
  return { ...values, actionNeededByTime: '' }
}
export function clearLastScanDate(values) {
  return setLastScanDate(values, '')
}
// Today in America/New_York: the Last Scan Date's latest allowed value.
export function todayEastern(now = new Date()) {
  return easternDate(now)
}

// Order Details as segments: { text } plain, or { text, href } for a
// recognised reference the server resolved to the order's portal link (the
// contact header's destination). An unresolved reference stays plain text.
export function orderDetailsSegments(text, orderLinks = {}) {
  return splitOrderRefs(text).map((p) => (p.ref && orderLinks?.[p.ref]?.url ? { text: p.text, href: orderLinks[p.ref].url } : { text: p.text }))
}

// One change-log row's text: "Description: old → new", or "Created".
export const TASK_FIELD_LABELS = Object.freeze({
  created: 'Created',
  description: 'Description',
  orderDetails: 'Order Number',
  actionNeededByDate: 'Action Needed By Date',
  actionNeededByTime: 'Action Needed By Time',
  dueTrigger: 'Action Needed By',
  status: 'Status',
  closeNote: 'Close note',
})
export function taskEventText(e) {
  // Created from a Support contact (2026-10-15): its number in newValue.
  if (e.field === 'created') return e.newValue ? `Created from contact ${e.newValue}` : 'Created'
  // A note rather than a change (2026-10-13): "Action Needed By set from 5th
  // Business Day".
  if (e.field === 'note') return e.newValue ?? ''
  // Mark Task Completed's required note (2026-10-14).
  if (e.field === 'closeNote') return `Marked completed: ${e.newValue ?? ''}`
  const label = TASK_FIELD_LABELS[e.field] ?? e.field
  const show = (v) => (v == null || v === '' ? '(blank)'
    : e.field === 'status' ? (TASK_STATUS_LABELS[v] ?? v)
      : e.field === 'dueTrigger' ? (taskDueTriggerDisplay(v) ?? v)
        : v)
  return `${label}: ${show(e.oldValue)} → ${show(e.newValue)}`
}

// "Support Tasks → Create Task" in a contact's Other Actions (2026-10-15):
// shown on exactly the contacts the Queue's Support toggle shows
// (conversationMatchesToggles() in @cxhub/shared, with the viewer's task
// rule), for someone who can create tasks (an admin, or a csr with Tasks).
export function canCreateTaskFromContact(conversation, { viewer = null, canCreateTasks = false } = {}) {
  if (!conversation || !canCreateTasks) return false
  return conversationMatchesToggles(conversation, ['Support'], viewer)
}
